import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { api } from '@/services/api';

/**
 * On a demo instance only (the server decides: GET /api/instance, a database whose name says demo or
 * DEMO_INSTANCE=true), a band across every page, signed in or not: the companies, people and figures are invented.
 * Screenshots and visitors can't mistake demo data for a client's. Never dismissible. Nothing shows anywhere else,
 * or while the answer is unknown.
 * A guest (create_demo_guest.py: a real person, last name "Demo") is also told they may change anything, and that what
 * they do is tracked under that demo name, so it stays confidential (Davin, 16 Oct).
 */
export const useDemoInstance = () => {
  const q = useQuery<{ demo: boolean }>({
    queryKey: ['instance'], queryFn: () => api.get('/api/instance').then(r => r.data),
    staleTime: Infinity, retry: false,
  });
  return q.data?.demo === true;
};

export const GUEST_LAST_NAME = 'Demo';

const DemoBand: React.FC<{ className?: string; user?: { first_name?: string; last_name?: string } }> = ({
  className = '', user,
}) => {
  if (!useDemoInstance()) return null;
  const guest = user?.last_name === GUEST_LAST_NAME && user.first_name ? `${user.first_name} ${GUEST_LAST_NAME}` : null;
  return (
    // C64 (a11y): inside a named region, so no page content sits outside a landmark (axe 'region')
    <section aria-label="Demo notice" className={className}>
      <div role="note" aria-label="Demo" data-testid="demo-band"
           className="flex items-center justify-center gap-mp-2 border-b border-mp-warning bg-mp-warning-bg px-mp-3 py-1 text-center text-mp-sm font-medium text-mp-warning">
        <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          <b>Demo: invented data.</b> Every company, person and figure here is made up.
          {guest && <> You're welcome to change anything. To keep it confidential, your activity is tracked under
            the name <b>{guest}</b>.</>}
        </span>
      </div>
    </section>
  );
};

export default DemoBand;
